/**
 * useEffectiveUser — collapses (real admin, impersonation target) to
 * the identity the current view should reflect.
 *
 * Use this anywhere you'd otherwise call `useAuth().user` for
 * role/FA gating. `useAuth().user` itself remains the REAL admin
 * (per Axios interceptor skip on /auth/me).
 */

import { useAuth } from '../context/AuthContext'
import { useImpersonation } from '../context/ImpersonationContext'

export interface EffectiveUser {
  id: number
  name: string
  email: string
  role: 'member' | 'talent' | 'council' | 'admin' | 'super_admin'
  focus_area_code?: string | null
  preferred_focus_areas?: string[]
  // Profile-page fields
  bio?: string | null
  github_handle?: string | null
  visible_in_directory?: boolean
  talent_profile_active?: boolean
  /** LT viewer (ticket #18): cross-FA read-only vision. */
  views_all_demands?: boolean
  /** Set once a no-FA council answered "I do not lead one focus area" (ticket #27). */
  onboarding_completed_at?: string | null
  /** The DSAI cluster this person leads, if any. */
  dsai_cluster_lead?: string | null
  // MS Graph enrichment
  job_title?: string | null
  department?: string | null
  office_location?: string | null
  company?: string | null
  country?: string | null
}

export function useEffectiveUser(): EffectiveUser | null {
  const { user } = useAuth()
  const { target } = useImpersonation()
  if (target) {
    return {
      id: target.id,
      name: target.name,
      email: target.email,
      role: target.role,
      focus_area_code: target.focus_area_code ?? null,
      preferred_focus_areas: target.preferred_focus_areas ?? [],
      bio: target.bio ?? null,
      github_handle: target.github_handle ?? null,
      visible_in_directory: target.visible_in_directory ?? true,
      talent_profile_active: target.talent_profile_active ?? false,
      views_all_demands: false,  // impersonation targets never carry LT vision
      onboarding_completed_at: null,  // View-as never shows the FA dialog anyway
      dsai_cluster_lead: target.dsai_cluster_lead ?? null,
      job_title: target.job_title ?? null,
      department: target.department ?? null,
      office_location: target.office_location ?? null,
      company: target.company ?? null,
      country: target.country ?? null,
    }
  }
  if (!user) return null
  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role as EffectiveUser['role'],
    focus_area_code: user.focus_area_code ?? null,
    preferred_focus_areas: user.preferred_focus_areas ?? [],
    bio: user.bio ?? null,
    github_handle: user.github_handle ?? null,
    visible_in_directory: user.visible_in_directory ?? true,
    talent_profile_active: user.talent_profile_active ?? false,
    views_all_demands: user.views_all_demands ?? false,
    onboarding_completed_at: user.onboarding_completed_at ?? null,
    dsai_cluster_lead: user.dsai_cluster_lead ?? null,
    job_title: user.job_title ?? null,
    department: user.department ?? null,
    office_location: user.office_location ?? null,
    company: user.company ?? null,
    country: user.country ?? null,
  }
}
